// ABOUTME: Utility functions for picking which chore list to surface on compact dashboard cards,
// ABOUTME: and shared business rules (like the toggle-lock guard) used across chore-list cards.

import type { ChoreListPeriodStatus } from '../types/api';
import { ChoreListType, ChoreListStatus } from '../types/api';

/**
 * Grace period (ms) before a chore-list UI change takes visible effect — auto-collapsing a
 * completed list (issue #1515), and delaying a newly-created pinned list's insertion or the
 * Next Responsibility card's swap to the next list (issue #1916). Shared so every such delay
 * stays in sync.
 */
export const CHORE_LIST_DELAY_MS = 5000;

/**
 * Normalizes a repeat frequency from the backend enum (the API may send the number or the name) to the name
 * Kuleana labels a list with. Shared so every card that shows the chip reads the same.
 */
export const normalizeRepeatFrequency = (value: string | number): string => {
  if (typeof value === 'number') {
    switch (value) {
      case 0: return 'Daily';
      case 1: return 'Weekly';
      case 2: return 'Monthly';
      default: return 'Daily';
    }
  }
  return value;
};

/**
 * Returns the first incomplete chore list in array order, or undefined if every list is
 * complete (or the list is empty). Used for the "next responsibility" mini card on the
 * All Kids dashboard tab (issue #1900).
 */
export function pickNextChoreList(choreLists: ChoreListPeriodStatus[]): ChoreListPeriodStatus | undefined {
  return choreLists.find(list => !list.isComplete);
}

/**
 * Returns true when a chore list is finalized and its type/flag requires parent
 * approval before it can be re-toggled — MakeItRight, ResetAndReflect, OneOnOneTime,
 * and any Regular list with requiresApproval set. Shared by every dashboard card that
 * renders chore-list checkboxes, so the "locked from toggling" rule can't drift between
 * cards (issue #1908).
 */
export function isChoreListApproved(choreList: ChoreListPeriodStatus): boolean {
  return choreList.status === ChoreListStatus.Finalized &&
    (choreList.type === ChoreListType.MakeItRight ||
      choreList.type === ChoreListType.ResetAndReflect ||
      choreList.type === ChoreListType.OneOnOneTime ||
      choreList.requiresApproval);
}

const PINNED_TYPES = new Set([ChoreListType.MakeItRight, ChoreListType.ResetAndReflect, ChoreListType.OneOnOneTime]);

function choreListTypeBucket(choreList: ChoreListPeriodStatus): number {
  if (choreList.type === ChoreListType.MakeItRight || choreList.type === ChoreListType.ResetAndReflect) return 0;
  if (choreList.type === ChoreListType.OneOnOneTime) return 1;
  return 2;
}

/**
 * Sorts chore lists the same way the backend does: MakeItRight/ResetAndReflect first, then
 * OneOnOneTime, then Regular; pinned types are ordered by newest StartDate first; Regular lists
 * follow sortOrder; a final id tiebreak keeps the order deterministic when every other key ties.
 * Mirrors BePono.API/Services/ChoreListOrderingExtensions.cs — used by the dashboard's SignalR
 * reducers so a live-patched order never diverges from what a full reload would show (issue #1916).
 */
export function sortChoreStatuses(choreLists: ChoreListPeriodStatus[]): ChoreListPeriodStatus[] {
  return [...choreLists].sort((a, b) => {
    const bucketA = choreListTypeBucket(a);
    const bucketB = choreListTypeBucket(b);
    if (bucketA !== bucketB) return bucketA - bucketB;

    if (PINNED_TYPES.has(a.type) || PINNED_TYPES.has(b.type)) {
      const startDateA = PINNED_TYPES.has(a.type) ? (a.startDate ?? '') : '';
      const startDateB = PINNED_TYPES.has(b.type) ? (b.startDate ?? '') : '';
      if (startDateA !== startDateB) return startDateA < startDateB ? 1 : -1; // descending
    }

    const sortOrderA = a.sortOrder ?? 0;
    const sortOrderB = b.sortOrder ?? 0;
    if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;

    return a.id - b.id;
  });
}

/**
 * Replaces every chore list of the given type in `current` with `updated`, then re-sorts the
 * whole array via sortChoreStatuses — the correct way to fold a MakeItRightUpdated /
 * ResetAndReflectUpdated / OneOnOneTimeUpdated SignalR payload into dashboard state, since a
 * naive "prepend the new lists" merge can diverge from the backend's true order (issue #1916).
 */
export function mergeChoreStatusesByType(
  current: ChoreListPeriodStatus[],
  updated: ChoreListPeriodStatus[],
  type: ChoreListType
): ChoreListPeriodStatus[] {
  const others = current.filter(s => s.type !== type);
  return sortChoreStatuses([...others, ...updated]);
}
